import type { RichContentBlock } from "@/content/contentSchema";

// The Privacy Policy, as published at /privacy. Written from what the site
// and its API actually collect; a change to what is collected or shared
// changes this text and its last-updated date.
export const privacyPage = {
  seoTitle: "Privacy Policy | General Applications",
  seoDescription:
    "What General Applications collects through this website and the contact form, why, who processes it, and how to reach us about your information.",
  eyebrow: "Legal",
  headline: "Privacy Policy",
  heroCopy: [
    "What this website collects, why, who handles it for us, and the choices you have. The short version: we collect what you send through the contact form and nothing else about you.",
    "Last updated September 30, 2026.",
  ],
  content: [
    { type: "heading", level: 2, text: "Who we are" },
    {
      type: "paragraph",
      text: 'This website, generalapplications.com, is run by General Applications LLC ("we", "us"). Questions about this policy go to contact@generalapplications.com.',
    },
    { type: "heading", level: 2, text: "What we collect" },
    {
      type: "list",
      items: [
        "**The contact form.** When you send a message, we receive the name, work email, organization, role or team, focus area and message you enter.",
        "**Your IP address, briefly.** The server holds it in memory for ten minutes to limit how often the contact form can be sent. It is not written to a database and is gone when the server restarts.",
        "**Server logs.** Our hosting provider keeps standard request logs, such as IP address, page requested and browser type, to run and secure the service.",
      ],
    },
    {
      type: "paragraph",
      text: "We do not use analytics, advertising trackers or cookies. Fonts, images and scripts are served from this site, not from third parties.",
    },
    { type: "heading", level: 2, text: "How we use it" },
    {
      type: "paragraph",
      text: "We use a contact-form message to reply to you and to discuss the work you asked about. We use logs and the temporary IP record only to keep the site running and to stop abuse. We do not sell personal information or use it for advertising.",
    },
    { type: "heading", level: 2, text: "Who handles it for us" },
    {
      type: "list",
      items: [
        "**SendGrid, part of Twilio**, delivers contact-form messages to our inbox.",
        "**Google Workspace** hosts that inbox.",
        "**DigitalOcean** hosts this website and its server.",
      ],
    },
    {
      type: "paragraph",
      text: "We may also share information when the law requires it, to protect someone's safety, or as part of a sale or reorganization of our business, which would keep this policy's protections.",
    },
    { type: "heading", level: 2, text: "Our social media pages" },
    {
      type: "paragraph",
      text: "We share our articles on our company pages on social networks such as LinkedIn. When you interact with us on those networks, their own privacy policies apply.",
    },
    { type: "heading", level: 2, text: "How long we keep it" },
    {
      type: "paragraph",
      text: "Contact-form messages stay in our inbox for as long as they are useful for the conversation and our business records. Ask us and we will delete yours unless the law requires us to keep it.",
    },
    { type: "heading", level: 2, text: "Your choices" },
    {
      type: "paragraph",
      text: "To see, correct or delete information you have sent us, email contact@generalapplications.com from the address you used. We will answer within 30 days. If your state gives you further privacy rights, we will honor them.",
    },
    { type: "heading", level: 2, text: "Local storage" },
    {
      type: "paragraph",
      text: "If you choose a light or dark theme, your browser remembers that choice on your own device. It is never sent to us.",
    },
    { type: "heading", level: 2, text: "Children" },
    {
      type: "paragraph",
      text: "This site is for businesses and is not directed to children under 13. If you think a child has sent us information, tell us and we will delete it.",
    },
    { type: "heading", level: 2, text: "Changes" },
    {
      type: "paragraph",
      text: "When this policy changes, the date at the top of this page changes with it.",
    },
  ] satisfies RichContentBlock[],
};
